import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { Device } from '@/types/api';
import { Badge } from '@/components/ui/badge';
import { SignalIcon } from '@/components/ui/signal-icon';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { SortToolbar } from '@/components/ui/sort-toolbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, formatRelativeTime } from '@/lib/utils';
import { deviceTypeLabel, formatUnitOfMeasureSuffix } from '@/features/devices/utils/salt-config';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { hasFreshTransmission } from '@/features/devices/utils/has-fresh-transmission';
import type { DeviceSortBy, SortDir } from '@/features/devices/api/devices-api';
import { deviceStatus } from '../utils/device-status';

interface PropertyDevicesTableProps {
  devices: Device[];
  propertyId: string;
  // Sort state mirrors what's already in usePropertyPortal; passing it
  // down keeps the column headers as the canonical sort affordance and
  // matches the Units table's pattern.
  sortBy: DeviceSortBy;
  sortDir: SortDir;
  onSortByChange: (next: DeviceSortBy) => void;
  onSortDirChange: (next: SortDir) => void;
}

// List-view counterpart to PropertyDeviceCard. Same fields, tabular layout
// so installers managing 100+ meters can scan a property at a glance
// instead of paging through a card grid. The Grid/List toggle on the
// Property Portal flips between this and the card grid; per-user
// persistence via useViewMode.
//
// Only the columns that map to a server-side sort key are clickable
// (Serial → 'unit', Last Seen → 'createdAt'). The other columns render
// as plain headers so the affordance is honest — there's no client-side
// sort fallback on data we can't paginate against.
export function PropertyDevicesTable({
  devices,
  propertyId,
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirChange,
}: PropertyDevicesTableProps) {
  const navigate = useNavigate();

  function handleSort(nextSortBy: DeviceSortBy) {
    if (sortBy === nextSortBy) {
      onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onSortByChange(nextSortBy);
      onSortDirChange('asc');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
        <SortToolbar
          sortBy={sortBy}
          sortDir={sortDir}
          onSortByChange={onSortByChange}
          onSortDirChange={onSortDirChange}
          options={[
            { value: 'unit', label: 'Unit number' },
            { value: 'createdAt', label: 'Created' },
          ]}
        />
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
        <Table density="compact">
        <TableHeader>
          <TableRow>
            <SortableTableHead
              label="Serial"
              active={sortBy === 'unit'}
              direction={sortDir}
              onClick={() => handleSort('unit')}
            />
            <TableHead>Device Type</TableHead>
            <TableHead>Building</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead className="text-right">Reading</TableHead>
            <TableHead>Signal</TableHead>
            <TableHead>Active Alarms</TableHead>
            <SortableTableHead
              label="Last Seen"
              active={sortBy === 'createdAt'}
              direction={sortDir}
              onClick={() => handleSort('createdAt')}
            />
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => {
            const tx = device.latestTransmission;
            const isFresh = hasFreshTransmission(device);
            const reading = isFresh && tx ? (tx.computedWaterTotalization ?? tx.waterTotalization) : null;
            const readingUnit = formatUnitOfMeasureSuffix(tx?.computedUnitOfMeasure ?? tx?.unitOfMeasure);
            const alerts = device.activeAlerts ?? [];
            const hasAlarms = alerts.length > 0;
            const primaryAlert = hasAlarms ? alerts[0] : null;
            const status = deviceStatus(device);
            const isWaitingForJoin = status === 'waiting' && device.unitAssignedAt != null;
            return (
              <TableRow
                key={device.id}
                onClick={() => navigate(deviceDetailPath(device.id, { kind: 'property', id: propertyId }))}
                className={cn(
                  'cursor-pointer group',
                  hasAlarms && 'border-l-[3px] border-l-error-500',
                )}
              >
                <TableCell className="font-mono text-xs">
                  {device.serialNumber}
                </TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {deviceTypeLabel(device)}
                </TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300">
                  {device.buildingName ?? <span className="italic text-gray-400">Unassigned</span>}
                </TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300">
                  {device.unitNumber ?? <span className="italic text-gray-400">—</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {reading != null ? (
                    <>
                      <span className="font-semibold">{reading.toLocaleString()}</span>
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{readingUnit}</span>
                    </>
                  ) : (
                    <span className="text-xs italic text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {isFresh ? (
                    <SignalIcon
                      rssi={device.latestTransmission?.rssi}
                      meta={{
                        snr: device.latestTransmission?.snr,
                        spreadingFactor: device.latestTransmission?.spreadingFactor,
                        gatewayEui: device.latestTransmission?.gatewayEui,
                        timestamp: device.latestTransmission?.timestamp ?? device.lastSeenAt,
                      }}
                    />
                  ) : (
                    <span className="text-xs italic text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {hasAlarms && primaryAlert ? (
                    <div className="flex items-center gap-1">
                      <Badge variant="error" size="sm">
                        <AlertTriangle className="w-3 h-3" />
                        <span className="truncate max-w-[140px]">{primaryAlert.displayLabel}</span>
                      </Badge>
                      {alerts.length > 1 && (
                        <Badge variant="error" size="sm">+{alerts.length - 1}</Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {device.lastSeenAt ? formatRelativeTime(device.lastSeenAt) : <span className="italic">Never</span>}
                </TableCell>
                <TableCell>
                  {isWaitingForJoin ? (
                    <Badge variant="error" size="sm">Awaiting Join</Badge>
                  ) : isFresh ? (
                    <Badge variant="success" size="sm" dot>Active</Badge>
                  ) : (
                    <Badge variant="default" size="sm">Awaiting uplink</Badge>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
